import { LSPClient, DEFAULT_CONFIG } from "./lsp_core";
import { detectDominantLanguages, getLanguageFileExtensions } from "./os_utils";
import { toFileUri } from "./file_utils";
import * as path from "path";
import * as fs from "fs";
import { glob } from "glob";

export interface Diagnostic {
  file: string;
  line: number;
  column: number;
  severity: "error" | "warning" | "info" | "hint";
  message: string;
  code?: string | number;
  source?: string;
}

function lspSeverityToString(sev: number): Diagnostic["severity"] {
  switch (sev) {
    case 1:
      return "error";
    case 2:
      return "warning";
    case 3:
      return "info";
    case 4:
      return "hint";
    default:
      return "info";
  }
}

export async function collectDiagnostics({
  projectRoot = process.cwd(),
  file = "",
  waitMs = 800,
}: {
  projectRoot?: string;
  file?: string;
  waitMs?: number;
} = {}): Promise<Diagnostic[]> {
  const rootUri = toFileUri(projectRoot);
  const client = new LSPClient(rootUri, {
    ...DEFAULT_CONFIG,
    timeout: 60000,
    logLevel: "debug",
  });

  const languages = detectDominantLanguages(projectRoot, {
    ignoredDirs: new Set(["node_modules", "dist"]),
    topN: 1,
  });
  const lang = languages[0];
  const file_ext = getLanguageFileExtensions(lang);

  try {
    await client.start(lang);
    let files: string[] = [];
    if (file) {
      files = [path.isAbsolute(file) ? file : path.join(projectRoot, file)];
    } else {
      files = await glob(`**/*.{${file_ext}}`, {
        cwd: projectRoot,
        ignore: "node_modules/**",
        absolute: true,
      });
    }

    const allDiagnostics: Diagnostic[] = [];
    const seen = new Set<string>();

    function handleDiagnostics({
      uri,
      diagnostics,
    }: {
      uri: string;
      diagnostics: any[];
    }) {
      const relativePath = path.relative(
        projectRoot,
        uri.replace(/^file:\/\//, ""),
      );
      for (const d of diagnostics) {
        const key = `${relativePath}:${d.range?.start?.line}:${d.range?.start?.character}:${d.message}`;
        if (seen.has(key)) continue;
        seen.add(key);
        allDiagnostics.push({
          file: relativePath,
          line: d.range?.start?.line + 1 || 1,
          column: d.range?.start?.character + 1 || 1,
          severity: lspSeverityToString(d.severity),
          message: d.message,
          code: d.code,
          source: d.source,
        });
      }
    }

    client.on("diagnostics", handleDiagnostics);

    for (const filePath of files) {
      const fileUri = toFileUri(filePath);
      const content = fs.readFileSync(filePath, "utf-8");
      await client.sendDidOpen(fileUri, content, lang);
    }

    // 等待 LSP 推送 diagnostics
    await new Promise((resolve) => setTimeout(resolve, waitMs));

    for (const filePath of files) {
      const fileUri = toFileUri(filePath);
      await client.sendDidClose(fileUri);
    }

    return allDiagnostics;
  } finally {
    await client.shutdown();
  }
}
